/**
 * Pure helpers for the ordered home-page video sets. Each audience
 * (educator, partner, both) has its own set, read from the videos' `slots`.
 * A set is a list of video ids; index 0 is slot 1 (the featured card). Every
 * helper returns a new array and never mutates its input. The audience's full
 * list is what gets sent to PUT /staff/videos/home-order.
 */

export const MAX_HOME_VIDEOS = 5;

/**
 * The video's slot position in an audience's set, or null.
 *
 * @param {{ slots?: Array<{ audience: string, position: number }> }} video
 * @param {string} audience
 * @returns {number|null}
 */
export function getSlotPosition(video, audience) {
  const slot = (Array.isArray(video?.slots) ? video.slots : [])
    .find((item) => item && item.audience === audience);
  return slot && Number.isInteger(slot.position) && slot.position > 0 ? slot.position : null;
}

/**
 * An audience's home videos, ordered by slot position.
 *
 * @param {import('@/features/staff-settings-videos/hooks/useStaffVideos').StaffVideo[]} videos
 * @param {string} audience
 */
export function getHomeVideos(videos = [], audience) {
  return videos
    .filter((video) => getSlotPosition(video, audience) !== null)
    .sort((a, b) => getSlotPosition(a, audience) - getSlotPosition(b, audience));
}

/** @param {number[]} ids */
export function canPin(ids) {
  return ids.length < MAX_HOME_VIDEOS;
}

/**
 * Adds a video to the end of the home set.
 *
 * @param {number[]} ids
 * @param {number} id
 * @returns {{ ids: number[], error: null|'full'|'already_pinned' }}
 */
export function pinVideo(ids, id) {
  if (ids.includes(id)) return { ids: [...ids], error: 'already_pinned' };
  if (!canPin(ids)) return { ids: [...ids], error: 'full' };
  return { ids: [...ids, id], error: null };
}

/**
 * @param {number[]} ids
 * @param {number} id
 * @returns {number[]}
 */
export function unpinVideo(ids, id) {
  return ids.filter((existing) => existing !== id);
}

function swap(ids, from, to) {
  if (from < 0 || from >= ids.length || to < 0 || to >= ids.length) return [...ids];
  const next = [...ids];
  [next[from], next[to]] = [next[to], next[from]];
  return next;
}

/**
 * Moves the id at `index` one slot toward slot 1. No-op at the top.
 *
 * @param {number[]} ids
 * @param {number} index
 */
export function moveUp(ids, index) {
  return swap(ids, index, index - 1);
}

/**
 * Moves the id at `index` one slot toward the end. No-op at the bottom.
 *
 * @param {number[]} ids
 * @param {number} index
 */
export function moveDown(ids, index) {
  return swap(ids, index, index + 1);
}

/**
 * Applies one audience's home set, as returned by PUT
 * /staff/videos/home-order, to the cached video list. Only that audience's
 * slots change: listed videos get their slot at the position given by the
 * response order, every other video loses its slot for that audience.
 * Slots for other audiences are kept as they are.
 *
 * @param {import('@/features/staff-settings-videos/hooks/useStaffVideos').StaffVideo[]|undefined} videos
 * @param {string} audience
 * @param {import('@/features/staff-settings-videos/hooks/useStaffVideos').StaffVideo[]} homeSet - in slot order
 */
export function applyHomeSet(videos, audience, homeSet) {
  if (!Array.isArray(videos) || !Array.isArray(homeSet)) return videos;
  const positions = new Map(homeSet.map((video, index) => [
    video.id,
    getSlotPosition(video, audience) ?? index + 1
  ]));

  return videos.map((video) => {
    const otherSlots = (Array.isArray(video.slots) ? video.slots : [])
      .filter((slot) => slot.audience !== audience);
    const slots = positions.has(video.id)
      ? [...otherSlots, { audience, position: positions.get(video.id) }]
      : otherSlots;
    return { ...video, slots };
  });
}
